<template>
  <div>
    <div ref="trigger">
      <slot name="trigger">
        <Button class="hidden md:block event-button">
          <PlusIcon class="h-3" /> New
        </Button>
      </slot>
    </div>
    <div
      ref="dropdownMenu"
      class="z-20 hidden bg-white rounded-lg shadow-sm w-52 dark:bg-gray-700"
    >
      <div class="flex *:cursor-pointer">
        <div
          class="w-6/12 justify-center flex-shrink-0 text-xs items-center flex flex-col gap-2 p-4 border-r-0 hover:bg-blue-50 hover:border-blue-200 hover:text-primary-700 border border-transparent rounded-s-lg"
          @click="toggleEventModal"
        >
          <CalendarCheck class="h-5" />
          <div>Event</div>
        </div>
        <div
          class="w-6/12 justify-center flex-grow-0 text-xs items-center flex flex-col gap-2 p-4 hover:bg-blue-50 hover:border-blue-200 hover:text-primary-700 border border-transparent rounded-e-lg"
          @click="toggleAppointmentModal"
        >
          <DeliveryTruckIcon class="h-5" />
          <div>Appointment</div>
        </div>
      </div>
    </div>
    <CalendarEventModal
      v-if="eventModal"
      auto-open
      :calendar-event="newEvent"
      @hide-modal="toggleEventModal"
      @created="refreshTimelineEvents"
    />
    <!-- Held back until the placement is settled: the two branches differ by
         more than a class, and the desktop one is the jQuery modal, which shows
         itself and registers a backdrop on mount. Mounting it and then swapping
         to a sheet strands that backdrop over the board. -->
    <AppointmentCreateModal
      v-if="appointmentModal && flagsResolved"
      :initial_date="appointmentInitialDate"
      :sheet="sheet"
      @hide-modal="toggleAppointmentModal"
      @created="refreshTimelineEvents"
    />
  </div>
</template>

<script setup lang="ts">
import type { DropdownInterface, DropdownOptions } from "flowbite";
import { Dropdown } from "flowbite";
import moment from "moment";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { trackMobileWebEvent } from "@/lib/pendo";

import Button from "@/components/Seranova/Buttons/Button.vue";
import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import CalendarCheck from "@/images/CalendarCheck.svg?component";
import DeliveryTruckIcon from "@/images/delivery_truck.svg?component";
import PlusIcon from "@/images/plus-outline.svg?component";
import CalendarEventModal from "@/Scheduler/CalendarEventModal.vue";
import { useEventCalendar } from "@/SchedulerV2/useEventCalendar";
import { newCalendarEventOn } from "@/typings/calendarEvent";

const props = withDefaults(
  defineProps<{
    defaultDate?: Date | string;
    // Both come from the call site rather than from useSheetPlacement here, for
    // the reason CalendarEventSurface records: this button is also on the
    // classic board (Scheduler/SchedulePage.vue), which must never show a sheet.
    // Reading placement here would give it one as a side effect.
    sheet?: boolean;
    // Whether the sheet-or-modal decision has actually been answered. Defaults
    // true so a call site that never opts into sheets — the classic board — is
    // not made to wait on a question it does not ask.
    flagsResolved?: boolean;
  }>(),
  { defaultDate: undefined, sheet: false, flagsResolved: true }
);

const dropdownMenu = ref<HTMLElement | null>(null);
const trigger = ref<HTMLElement | null>(null);
const dropdown = ref<DropdownInterface | null>(null);
const eventModal = ref(false);
const appointmentModal = ref(false);
const newEvent = ref(newCalendarEventOn(props.defaultDate));
const { refreshTimeline } = useEventCalendar();

// The appointment availability picker only searches today onward, so a past
// board date falls back to its own default (today)
const appointmentInitialDate = computed(() => {
  if (!props.defaultDate) return undefined;
  const day = moment(props.defaultDate);
  if (!day.isValid() || day.isBefore(moment(), "day")) return undefined;
  return day.toDate();
});

onMounted(() => {
  // options with default values
  const options: DropdownOptions = {
    placement: "bottom-end",
    triggerType: "click",
    offsetSkidding: 0,
    offsetDistance: 10,
    delay: 300
  };
  dropdown.value = new Dropdown(dropdownMenu.value, trigger.value, options);
});

const toggleEventModal = () => {
  if (!eventModal.value) {
    // Build the event at open time so the default date tracks in-page
    // navigation; the v-if remount below is what makes the modal pick it up.
    newEvent.value = newCalendarEventOn(props.defaultDate);
  }
  eventModal.value = !eventModal.value;
};

const toggleAppointmentModal = () => {
  appointmentModal.value = !appointmentModal.value;
  // Counted on the opening edge only, and only for the sheet: the event answers
  // "how are create sheets being reached", so a desktop modal open is not one of
  // them. trackMobileWebEvent stamps the layout, which is what makes the phone
  // filter the ticket asks for possible.
  if (appointmentModal.value && props.sheet) {
    trackMobileWebEvent("create_sheet_open", { source: "fob" });
  }
};

const refreshTimelineEvents = () => {
  refreshTimeline();
};

const onKeydown = (e: KeyboardEvent) => {
  if (e.key !== "Escape") return;
  dropdown.value?.hide();
};

onMounted(() => {
  document.addEventListener("keydown", onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped></style>
